import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiLogIn, FiLogOut, FiArrowLeft, FiTrash2, FiUsers } from 'react-icons/fi';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/customToast';
import CreateTenantModal from '../components/modals/CreateTenantModal';
import JoinTenantModal from '../components/modals/JoinTenantModal';
import DeleteTenantModal from '../components/modals/DeleteTenantModal';
import usePageTitle from '../hooks/usePageTitle';

const ManageOrganizations = () => {
    usePageTitle('Mes organisations');
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showJoinModal, setShowJoinModal] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const { getAuthHeaders, switchTenant, activeTenantId } = useAuth();
    const navigate = useNavigate();

    const fetchTenants = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/my`, {
                headers: getAuthHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                // Vérifier que data est bien un tableau
                if (Array.isArray(data)) {
                    setTenants(data);
                } else {
                    console.error('Invalid data format:', data);
                    toast.error('Format de données invalide');
                    setTenants([]);
                }
            } else {
                toast.error('Erreur lors du chargement des organisations');
            }
        } catch (error) {
            console.error('Error fetching tenants:', error);
            toast.error('Erreur réseau');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const handleSwitchTenant = async (tenantId) => {
        try {
            await switchTenant(tenantId);
            toast.success('Organisation changée avec succès');
            // Recharger la liste pour mettre à jour les badges "ACTIVE"
            await fetchTenants();
            // Rediriger vers les messages
            navigate('/messages');
        } catch (error) {
            console.error('Error switching tenant:', error);
            toast.error('Erreur lors du changement d\'organisation');
        }
    };

    const handleLeaveConfirm = async () => {
        if (!selectedTenant) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants/${selectedTenant._id}/leave`,
                {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                }
            );

            if (response.ok) {
                const isLastMember = selectedTenant.memberCount === 1;
                toast.success(
                    isLastMember 
                        ? 'Organisation supprimée avec succès'
                        : 'Vous avez quitté l\'organisation'
                );
                setShowLeaveModal(false);
                setSelectedTenant(null);
                
                // Si c'était le tenant actif, recharger la page
                if (selectedTenant._id === activeTenantId) {
                    window.location.href = '/join-tenant';
                } else {
                    fetchTenants();
                }
            } else {
                const data = await response.json();
                toast.error(data.message || 'Erreur lors de l\'opération');
            }
        } catch (error) {
            console.error('Error leaving tenant:', error);
            toast.error('Erreur réseau');
        }
    };

    const getPlanBadge = (plan) => {
        const colors = {
            free: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
            starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            team: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
            business: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            enterprise: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
        };
        return plan === 'free'
            ? `${colors.free} border border-red-400` // highlight free plan
            : colors[plan] || colors.free;
    };

    const getRoleLabel = (role) => {
        const labels = {
            owner: 'Propriétaire',
            admin: 'Administrateur',
            member: 'Membre'
        };
        return labels[role] || role;
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            owner: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
            admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            member: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        };
        return colors[role] || colors.member;
    };

    const getTypeBadge = (type) => {
        return type === 'personal'
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
            : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
    };

    const canLeaveTenant = (tenant) => {
        // Si l'utilisateur n'est pas owner, il peut toujours quitter
        if (tenant.userRole !== 'owner') return true;

        // Si owner unique avec d'autres membres, ne peut pas quitter
        if (!tenant.hasOtherOwners && tenant.memberCount > 1) {
            return false;
        }

        // Si owner avec d'autres owners OU seul membre, peut quitter
        return true;
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Header />
            <div className="flex-1 container mx-auto px-4 pt-4 sm:pt-8">
                {/* Bouton retour */}
                <button
                    onClick={() => navigate('/messages')}
                    className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition"
                >
                    <FiArrowLeft /> Retour aux messages
                </button>

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-black dark:text-white mb-2">
                        Gérer mes organisations
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Créez, rejoignez ou gérez vos organisations professionnelles et personnelles.
                    </p>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center justify-center gap-3 bg-primary text-white dark:text-black font-semibold py-4 px-6 rounded-lg hover:bg-primary-hover transition shadow-md"
                    >
                        <FiPlus className="h-5 w-5" />
                        Créer une organisation
                    </button>
                    <button
                        onClick={() => setShowJoinModal(true)}
                        className="flex items-center justify-center gap-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-semibold py-4 px-6 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition shadow-md border dark:border-gray-700"
                    >
                        <FiLogIn className="h-5 w-5" />
                        Rejoindre par code
                    </button>
                </div>

                {/* Organizations list */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement...</p>
                    </div>
                ) : tenants.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
                        <FiUsers className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                            Aucune organisation
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Créez votre première organisation ou rejoignez-en une existante.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {tenants.map((tenant) => (
                            <div
                                key={tenant._id}
                                className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition ${
                                    tenant.isActive
                                        ? 'ring-2 ring-primary'
                                        : 'hover:shadow-lg'
                                }`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="text-xl font-bold text-black dark:text-white truncate">
                                                {tenant.name}
                                            </h3>
                                            {tenant.isActive && (
                                                <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded font-semibold">
                                                    ACTIVE
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                            <span className={`text-xs px-2 py-1 rounded font-semibold ${getTypeBadge(tenant.type)}`}>
                                                {tenant.type === 'personal' ? 'Personnel' : 'Professionnel'}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded font-semibold uppercase ${getPlanBadge(tenant.plan)}`}>
                                                {tenant.plan}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded font-semibold ${getRoleBadgeColor(tenant.userRole)}`}>
                                                {getRoleLabel(tenant.userRole)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {tenant.memberCount} {tenant.memberCount > 1 ? 'membres' : 'membre'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {!tenant.isActive && (
                                        <button
                                            onClick={() => handleSwitchTenant(tenant._id)}
                                            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 bg-primary text-white dark:text-black font-semibold py-2 px-4 rounded hover:bg-primary-hover transition"
                                        >
                                            <FiLogIn className="h-4 w-4" />
                                            Basculer
                                        </button>
                                    )}
                                    {canLeaveTenant(tenant) ? (
                                        <button
                                            onClick={() => {
                                                setSelectedTenant(tenant);
                                                setShowLeaveModal(true);
                                            }}
                                            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-semibold py-2 px-4 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition"
                                        >
                                            {tenant.memberCount === 1 ? (
                                                <>
                                                    <FiTrash2 className="h-4 w-4" />
                                                    Supprimer
                                                </>
                                            ) : (
                                                <>
                                                    <FiLogOut className="h-4 w-4" />
                                                    Quitter
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button
                                            disabled
                                            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 font-semibold py-2 px-4 rounded cursor-not-allowed"
                                            title="Nommez un autre propriétaire pour quitter"
                                        >
                                            <FiLogOut className="h-4 w-4" />
                                            Quitter
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Footer />

            {/* Modals */}
            {showCreateModal && (
                <CreateTenantModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        fetchTenants();
                    }}
                />
            )}

            {showJoinModal && (
                <JoinTenantModal
                    onClose={() => setShowJoinModal(false)}
                    onSuccess={() => {
                        setShowJoinModal(false);
                        fetchTenants();
                    }}
                />
            )}

            {showLeaveModal && selectedTenant && (
                <DeleteTenantModal
                    open={showLeaveModal}
                    onClose={() => {
                        setShowLeaveModal(false);
                        setSelectedTenant(null);
                    }}
                    tenant={selectedTenant}
                    onConfirm={handleLeaveConfirm}
                    isOwner={selectedTenant.userRole === 'owner'}
                    isLastMember={selectedTenant.memberCount === 1}
                    hasOtherOwners={selectedTenant.hasOtherOwners}
                />
            )}
        </div>
    );
};

export default ManageOrganizations;
